import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';

/*
 * HeroPipeline — Cinematic 4-phase SVG animation
 *
 * Phase 0  DESIGN   — 3 nodes (API → Logic Builder → Database) matching WorkflowAnimation
 * Phase 1  GENERATE — Java code types out in editor chrome
 * Phase 2  COMPILE  — Bytecode hex cascade + progress bar
 * Phase 3  DEPLOY   — Server infrastructure lights up (3 servers + mesh)
 */

const PHASE_MS = [3200, 3600, 3000, 3800];
const TOTAL = PHASE_MS.reduce((a, b) => a + b, 0);

// ─── Icons (24×24 paths from WorkflowAnimation) ─────────
const ICO = {
  globe:    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
  database: 'M12 3C7.58 3 4 4.79 4 7v10c0 2.21 3.58 4 8 4s8-1.79 8-4V7c0-2.21-3.58-4-8-4zm0 2c3.87 0 6 1.5 6 2s-2.13 2-6 2-6-1.5-6-2 2.13-2 6-2zM6 17v-2.34c1.37.93 3.55 1.34 6 1.34s4.63-.41 6-1.34V17c0 .5-2.13 2-6 2s-6-1.5-6-2zm0-5v-2.34c1.37.93 3.55 1.34 6 1.34s4.63-.41 6-1.34V12c0 .5-2.13 2-6 2s-6-1.5-6-2z',
  swap:     'M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z',
  server:   'M4 1h16c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2zm0 8h16c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2zm0 8h16c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2zM19 4h-2v2h2V4zm0 8h-2v2h2v-2zm0 8h-2v2h2v-2z',
};

// ─── Code lines ─────────────────────────────────────────
const CODE_LINES = [
  { indent: 0, text: 'public class AlphanetixWorkflow1 {' },
  { indent: 0, text: '' },
  { indent: 1, text: '@Override' },
  { indent: 1, text: 'protected void execute() {' },
  { indent: 2, text: 'connect(source, target);' },
  { indent: 2, text: 'transform(payload);' },
  { indent: 2, text: 'validate(schema);' },
  { indent: 1, text: '}' },
  { indent: 0, text: '}' },
];

// ─── Bytecode hex ───────────────────────────────────────
const BYTE_ROWS = [
  'CA FE BA BE 00 00 00 3D',
  '00 2A 0A 00 02 00 03 07',
  '00 04 0C 00 05 00 06 01',
  '00 17 41 6C 70 68 61 6E',
  '65 74 69 78 57 6F 72 6B',
  '66 6C 6F 77 31 01 00 06',
  '3C 69 6E 69 74 3E 01 00',
  '03 28 29 56 01 00 04 43',
];

// ─── Java tokenizer for IDE-style syntax highlighting ──
const tokenizeLine = (text) => {
  const tokens = [];
  const re = /(@\w+)|(\b(?:public|class|protected|void)\b)|(AlphanetixWorkflow1)|(\b(?:connect|transform|validate|execute)\b)|(\b(?:source|target|payload|schema)\b)|([{}();,])|(\s+)|(\S+)/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const [full,,keyword, className, method, param, punct, ws] = m;
    let type = 'plain';
    if (m[1]) type = 'annotation';
    else if (keyword) type = 'keyword';
    else if (className) type = 'className';
    else if (method) type = 'method';
    else if (param) type = 'param';
    else if (punct) type = 'punct';
    else if (ws) type = 'ws';
    tokens.push({ text: full, type });
  }
  return tokens;
};

// Pre-tokenise all lines once
const CODE_TOKENS = CODE_LINES.map(l => ({
  indent: l.indent,
  text: l.text,
  tokens: tokenizeLine(l.text),
}));

// ─── Easing ─────────────────────────────────────────────
const ease = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

export default function HeroPipeline() {
  const { isLight } = useThemeMode();
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lockedPhase, setLockedPhase] = useState(null); // null = auto-cycle, 0-3 = loop single phase
  const startRef = useRef(performance.now());
  const rafRef = useRef(null);
  const cursorRef = useRef(true);
  const pausedRef = useRef(false);
  const pauseTimeRef = useRef(0);
  const lockedPhaseRef = useRef(null);
  const pipelineRef = useRef(null);
  const visibleRef = useRef(true);
  const lastTickRef = useRef(0);

  // ─── Palette ───────────────────────────────────────────
  const accent      = isLight ? '#6366f1' : '#10b981';
  const accentLight = isLight ? '#818cf8' : '#6ee7b7';
  const accentSoft  = isLight ? 'rgba(99, 102, 241,0.07)'  : 'rgba(16,185,129,0.08)';
  const accentLine  = isLight ? 'rgba(99, 102, 241,0.12)'  : 'rgba(16,185,129,0.18)';
  const textDim     = isLight ? 'rgba(15,23,42,0.28)'   : 'rgba(248,250,252,0.25)';
  const textMid     = isLight ? 'rgba(15,23,42,0.5)'    : 'rgba(248,250,252,0.5)';
  const textBright  = isLight ? '#334155' : '#cbd5e1';
  const codeBg      = isLight ? 'rgba(99, 102, 241,0.03)'  : 'rgba(16,185,129,0.04)';
  const labelColor  = isLight ? '#94a3b8' : '#64748b';
  const cardBg      = isLight ? 'rgba(255,255,255,0.45)' : 'rgba(2,6,23,0.35)';
  const cardBorder  = isLight ? 'rgba(255,255,255,0.6)'  : 'rgba(16,185,129,0.12)';

  // ─── WorkflowAnimation-matched node palette ───────────
  const httpAccent  = isLight ? '#6366f1' : '#a5b4fc';
  const httpGlow    = isLight ? 'rgba(99,102,241,0.08)' : 'rgba(165,180,252,0.08)';
  const httpIcon    = isLight ? '#6366f1' : '#c7d2fe';
  const dbAccent    = isLight ? '#10b981' : '#6ee7b7';
  const dbGlow      = isLight ? 'rgba(16,185,129,0.08)' : 'rgba(110,231,183,0.08)';
  const dbIcon      = isLight ? '#10b981' : '#a7f3d0';
  const mapBg       = isLight ? 'rgba(255,255,255,0.75)' : 'rgba(30,41,59,0.60)';
  const mapStroke   = isLight ? 'rgba(100,116,139,0.20)' : 'rgba(148,163,184,0.15)';
  const mapIcon     = isLight ? '#64748b' : '#94a3b8';
  const nodeBg      = isLight ? 'rgba(255,255,255,0.60)' : 'rgba(30,41,59,0.55)';
  const portColor   = isLight ? '#a5b4fc' : '#6ee7b7';
  const labelBg     = isLight ? 'rgba(255,255,255,0.70)' : 'rgba(15,23,42,0.55)';
  const labelStroke = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
  const labelText   = isLight ? '#475569' : '#e2e8f0';

  // ─── Syntax highlight palette (IDE-style) ─────────────
  const syn = {
    keyword:    isLight ? '#7c3aed' : '#c084fc',   // purple — public, class, protected, void
    annotation: isLight ? '#3730a3' : '#fbbf24',   // amber  — @Override
    className:  isLight ? '#0e7490' : '#22d3ee',   // cyan   — AlphanetixWorkflow1
    method:     isLight ? '#2563eb' : '#60a5fa',   // blue   — connect, transform, validate
    param:      isLight ? '#059669' : '#34d399',   // green  — source, target, payload, schema
    punct:      isLight ? '#94a3b8' : '#64748b',   // slate  — { } ( ) ; ,
    plain:      isLight ? '#334155' : '#cbd5e1',   // default text
    ws:         'transparent',
  };

  // ─── Deploy palette ────────────────────────────────────
  const serverOff   = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)';
  const serverOn    = isLight ? 'rgba(99, 102, 241,0.06)' : 'rgba(16,185,129,0.06)';
  const serverStroke = isLight ? 'rgba(99, 102, 241,0.15)' : 'rgba(16,185,129,0.18)';
  const dotOff      = isLight ? '#cbd5e1' : '#475569';
  const dotGreen    = isLight ? '#22c55e' : '#4ade80';
  const meshLine    = isLight ? 'rgba(99, 102, 241,0.10)' : 'rgba(16,185,129,0.14)';

  // Jump to a specific phase — locks animation to loop just that phase
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const jumpToPhase = useCallback((targetPhase) => {
    lockedPhaseRef.current = targetPhase;
    setLockedPhase(targetPhase);
    // Reset the phase-local timer so it starts from 0
    startRef.current = performance.now();
    setPhase(targetPhase);
    setProgress(0);
    // Ensure animation is running (unpause if paused)
    if (pausedRef.current) {
      pausedRef.current = false;
      setPaused(false);
      rafRef.current = requestAnimationFrame(tick);
    }
  }, []);

  // Toggle pause / play
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const togglePause = useCallback(() => {
    setPaused(prev => {
      const next = !prev;
      pausedRef.current = next;
      if (next) {
        pauseTimeRef.current = performance.now() - startRef.current;
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      } else {
        startRef.current = performance.now() - pauseTimeRef.current;
        rafRef.current = requestAnimationFrame(tick);
      }
      return next;
    });
  }, []);

  // Unlock — return to auto-cycling all phases
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const unlockPhases = useCallback(() => {
    lockedPhaseRef.current = null;
    setLockedPhase(null);
    startRef.current = performance.now();
    if (pausedRef.current) {
      pausedRef.current = false;
      setPaused(false);
      rafRef.current = requestAnimationFrame(tick);
    }
  }, []);

  // Animation loop — throttled to ~30fps, pauses when off-screen
  const tick = useCallback(() => {
    if (pausedRef.current) return;
    if (!visibleRef.current) { rafRef.current = null; return; }
    const now = performance.now();

    // Throttle to ~30fps
    if (now - lastTickRef.current < 33) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    lastTickRef.current = now;

    const locked = lockedPhaseRef.current;
    cursorRef.current = Math.floor(now / 530) % 2 === 0;

    if (locked !== null) {
      // Single-phase loop: progress cycles 0→1 over that phase's duration
      const dur = PHASE_MS[locked];
      const elapsed = (now - startRef.current) % dur;
      setPhase(locked);
      setProgress(Math.round(elapsed / dur * 100) / 100);
    } else {
      // Normal auto-cycle through all phases
      const elapsed = (now - startRef.current) % TOTAL;
      let acc = 0;
      for (let i = 0; i < PHASE_MS.length; i++) {
        if (elapsed < acc + PHASE_MS[i]) {
          setPhase(i);
          setProgress(Math.round((elapsed - acc) / PHASE_MS[i] * 100) / 100);
          break;
        }
        acc += PHASE_MS[i];
      }
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [tick]);

  // Pause animation when pipeline scrolls off-screen
  useEffect(() => {
    const el = pipelineRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
      if (entry.isIntersecting && !pausedRef.current && !rafRef.current) {
        startRef.current = performance.now();
        rafRef.current = requestAnimationFrame(tick);
      }
    }, { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [tick]);

  const LABELS = ['Design', 'Generate', 'Compile', 'Deploy'];
  const W = 480, H = 340;
  const CX = W / 2, CY = H / 2;

  // ─── Phase 0: Workflow node positions (left → right) ──
  const NODE_W = 76, NODE_H = 76, NODE_R = 18;
  const MAP_SZ = 40, MAP_R = 10;
  const GAP = 46;
  const totalW = NODE_W * 2 + MAP_SZ + GAP * 2;
  const startX = CX - totalW / 2;
  const nodeY = CY - NODE_H / 2 - 6;

  const apiNode  = { x: startX + NODE_W / 2, y: nodeY + NODE_H / 2 };
  const mapNode  = { x: startX + NODE_W + GAP + MAP_SZ / 2, y: nodeY + NODE_H / 2 };
  const dbNode   = { x: startX + NODE_W + GAP + MAP_SZ + GAP + NODE_W / 2, y: nodeY + NODE_H / 2 };

  // ─── Phase 3: Server positions ─────────────────────────
  const SRV_W = 88, SRV_H = 56, SRV_R = 12;
  const servers = [
    { x: CX - 130, y: CY - 55, label: 'Server 1' },
    { x: CX + 42,  y: CY - 55, label: 'Server 2' },
    { x: CX - 44,  y: CY + 35, label: 'Server 3' },
  ];

  return (
    <Box ref={pipelineRef} sx={{
      width: '100%',
      maxWidth: { xs: '100%', sm: '480px', lg: '520px' },
      mx: { xs: 'auto', lg: 0 },
    }}>
      {/* Phase pills + controls */}
      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center', alignItems: 'center', mb: 1.5 }}>
        {LABELS.map((label, i) => {
          const isActive = phase === i;
          const isLocked = lockedPhase === i;
          return (
            <Box key={label} onClick={() => isLocked ? unlockPhases() : jumpToPhase(i)} sx={{
              px: 1.5, py: 0.4,
              borderRadius: '10px',
              fontSize: '0.58rem',
              fontWeight: 700,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              fontFamily: "'DM Sans', sans-serif",
              color: isActive ? '#fff' : textMid,
              background: isActive ? accent : 'transparent',
              border: `1px solid ${isActive ? accent : accentLine}`,
              boxShadow: isActive
                ? isLight ? '0 2px 8px rgba(99, 102, 241,0.25)' : '0 2px 8px rgba(16,185,129,0.25)'
                : 'none',
              transition: 'all 0.5s cubic-bezier(0.4,0,0.2,1)',
              opacity: isActive ? 1 : 0.55,
              transform: isActive ? 'scale(1.05)' : 'scale(1)',
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': {
                opacity: 1,
                borderColor: accent,
              },
            }}>
              {label}
            </Box>
          );
        })}
        {/* Pause / Play toggle */}
        <Box onClick={togglePause} sx={{
          width: 24, height: 24, ml: 0.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%',
          border: `1px solid ${accentLine}`,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.3s ease',
          opacity: 0.5,
          '&:hover': { opacity: 1, borderColor: accent },
        }}>
          <svg width="10" height="10" viewBox="0 0 10 10" aria-label={paused ? 'Play' : 'Pause'}>
            {paused ? (
              <polygon points="2,1 9,5 2,9" fill={accent} />
            ) : (
              <>
                <rect x="1.5" y="1" width="2.5" height="8" rx="0.5" fill={accent} />
                <rect x="6" y="1" width="2.5" height="8" rx="0.5" fill={accent} />
              </>
            )}
          </svg>
        </Box>
      </Box>

      {/* Phase progress track */}
      <Box sx={{ display: 'flex', gap: '2px', px: 4, mb: 1.5 }}>
        {LABELS.map((_, i) => (
          <Box key={i} sx={{
            flex: PHASE_MS[i] / TOTAL,
            height: 2,
            borderRadius: 1,
            background: accentSoft,
            overflow: 'hidden',
            position: 'relative',
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              borderRadius: 1,
              background: accent,
              opacity: 0.7,
              width: phase === i ? `${progress * 100}%` : phase > i ? '100%' : '0%',
              transition: phase === i ? 'none' : 'width 0.3s ease',
            }} />
          </Box>
        ))}
      </Box>

      {/* Canvas */}
      <Box sx={{
        position: 'relative',
        background: isLight
          ? 'linear-gradient(145deg, rgba(255,255,255,0.82) 0%, rgba(243,244,255,0.65) 40%, rgba(238,237,253,0.55) 70%, rgba(255,255,255,0.75) 100%)'
          : 'linear-gradient(145deg, rgba(2,6,23,0.65) 0%, rgba(15,23,42,0.50) 50%, rgba(2,6,23,0.60) 100%)',
        backdropFilter: 'blur(32px) saturate(1.3)',
        WebkitBackdropFilter: 'blur(32px) saturate(1.3)',
        borderRadius: '20px',
        overflow: 'hidden',
        border: isLight
          ? '1px solid transparent'
          : `1px solid ${cardBorder}`,
        backgroundClip: 'padding-box',
        boxShadow: isLight
          ? '0 20px 60px rgba(99, 102, 241,0.08), 0 8px 24px rgba(99, 102, 241,0.04), 0 1px 3px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(99, 102, 241,0.04)'
          : '0 20px 60px rgba(0,0,0,0.30), 0 8px 24px rgba(0,0,0,0.20), 0 1px 3px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.03)',
        // Gradient border for light mode via pseudo-element
        ...(isLight && {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '20px',
            padding: '1.5px',
            background: 'linear-gradient(145deg, rgba(99, 102, 241,0.30), rgba(168,85,247,0.15), rgba(99, 102, 241,0.10), rgba(255,255,255,0.5))',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(ellipse at 30% 20%, rgba(99, 102, 241,0.04) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(168,85,247,0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
      }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} aria-hidden="true">
          <defs>
            <filter id="hp-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            <filter id="hp-glow-lg" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            <filter id="hp-shadow" x="-25%" y="-25%" width="150%" height="150%">
              <feDropShadow dx="0" dy="4" stdDeviation="10"
                floodColor={isLight ? 'rgba(99, 102, 241,0.07)' : 'rgba(0,0,0,0.30)'} floodOpacity="1" />
            </filter>
            <filter id="hp-shadow-db" x="-25%" y="-25%" width="150%" height="150%">
              <feDropShadow dx="0" dy="4" stdDeviation="10"
                floodColor={isLight ? 'rgba(16,185,129,0.07)' : 'rgba(0,0,0,0.30)'} floodOpacity="1" />
            </filter>
            <filter id="hp-shadow-map" x="-25%" y="-25%" width="150%" height="150%">
              <feDropShadow dx="0" dy="2" stdDeviation="6"
                floodColor={isLight ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.20)'} floodOpacity="1" />
            </filter>
            <filter id="hp-label-shadow" x="-15%" y="-15%" width="130%" height="130%">
              <feDropShadow dx="0" dy="1" stdDeviation="3"
                floodColor={isLight ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.20)'} floodOpacity="1" />
            </filter>
            <filter id="hp-srv-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="3" stdDeviation="8"
                floodColor={isLight ? 'rgba(99, 102, 241,0.06)' : 'rgba(0,0,0,0.25)'} floodOpacity="1" />
            </filter>
            {/* Gradient borders matching WorkflowAnimation */}
            <linearGradient id="hp-gHttp" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={httpAccent} stopOpacity="0.4" />
              <stop offset="100%" stopColor={portColor} stopOpacity="0.15" />
            </linearGradient>
            <linearGradient id="hp-gDb" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={dbAccent} stopOpacity="0.4" />
              <stop offset="100%" stopColor={portColor} stopOpacity="0.15" />
            </linearGradient>
          </defs>

          {/* ═══ PHASE 0 — DESIGN: API → Logic Builder → Database ═══ */}
          <g opacity={
            phase === 0 ? 1
            : phase === 1 ? Math.max(0, 1 - progress * 4)
            : 0
          }>
            {/* ── Connection lines ── */}
            {(() => {
              const lineSegs = [
                { x1: apiNode.x + NODE_W / 2, y1: apiNode.y, x2: mapNode.x - MAP_SZ / 2, y2: mapNode.y },
                { x1: mapNode.x + MAP_SZ / 2, y1: mapNode.y, x2: dbNode.x - NODE_W / 2, y2: dbNode.y },
              ];
              return lineSegs.map((seg, i) => {
                const lineLen = seg.x2 - seg.x1;
                const drawP = phase === 0
                  ? Math.max(0, Math.min(1, (progress - 0.18 - i * 0.18) * 3.5))
                  : 1;
                return (
                  <g key={`line-${i}`}>
                    <line x1={seg.x1} y1={seg.y1} x2={seg.x2} y2={seg.y2}
                      stroke={accentLine} strokeWidth="1.5"
                      strokeDasharray={`${drawP * lineLen},${lineLen}`}
                    />
                    {/* Arrow */}
                    <polygon
                      points={`${seg.x2 - 6},${seg.y2 - 3.5} ${seg.x2},${seg.y2} ${seg.x2 - 6},${seg.y2 + 3.5}`}
                      fill={accent} opacity={drawP > 0.8 ? (drawP - 0.8) * 5 * 0.4 : 0}
                    />
                    {/* Traveling pulse */}
                    {phase === 0 && progress > 0.5 && (
                      <circle
                        cx={seg.x1 + lineLen * ((progress * (2.2 + i * 0.3)) % 1)}
                        cy={seg.y1}
                        r="3"
                        fill={accentLight}
                        filter="url(#hp-glow)"
                        opacity={0.65}
                      />
                    )}
                  </g>
                );
              });
            })()}

            {/* ── API Node (Source) — matches WorkflowAnimation Node http ── */}
            {(() => {
              const ep = phase === 0 ? Math.min(1, Math.max(0, progress * 5)) : 1;
              const o = ease(ep);
              const ny = apiNode.y + (1 - o) * 10;
              const nx = apiNode.x;
              const hw = NODE_W / 2, hh = NODE_H / 2;
              return (
                <g opacity={o}>
                  {/* Breathing glow ring */}
                  <rect x={nx - hw - 2} y={ny - hh - 2} width={NODE_W + 4} height={NODE_H + 4}
                    rx={NODE_R + 2} fill="none" stroke={httpAccent} strokeWidth={0.8}
                    opacity={phase === 0 && progress > 0.5 ? Math.abs(Math.sin(progress * 8)) * 0.15 : 0}
                  />
                  {/* Card */}
                  <rect x={nx - hw} y={ny - hh} width={NODE_W} height={NODE_H}
                    rx={NODE_R} fill={nodeBg} stroke="url(#hp-gHttp)" strokeWidth={1.2}
                    filter="url(#hp-shadow)" />
                  {/* Icon halo */}
                  <circle cx={nx} cy={ny + 1} r={20} fill={httpGlow} />
                  <circle cx={nx} cy={ny + 1} r={15} fill="none" stroke={httpAccent} strokeWidth={0.6} opacity={0.15} />
                  {/* Globe icon */}
                  <g transform={`translate(${nx - 12},${ny - 11})`}>
                    <path d={ICO.globe} fill={httpIcon} />
                  </g>
                  {/* Connector dots */}
                  <circle cx={nx + hw} cy={ny} r={4} fill={portColor} />
                  {/* Label */}
                  <g>
                    <rect x={nx - 32} y={ny + hh + 10} width={64} height={22}
                      rx={11} fill={labelBg} stroke={labelStroke} strokeWidth={0.8}
                      filter="url(#hp-label-shadow)" />
                    <text x={nx} y={ny + hh + 25} textAnchor="middle"
                      fill={labelText} fontSize={10} fontWeight={600}
                      fontFamily="Montserrat, 'DM Sans', sans-serif">
                      API
                    </text>
                  </g>
                </g>
              );
            })()}

            {/* ── Logic Builder Node (Transform) — matches WorkflowAnimation MapNode ── */}
            {(() => {
              const ep = phase === 0 ? Math.min(1, Math.max(0, (progress - 0.12) * 5)) : 1;
              const o = ease(ep);
              const ny = mapNode.y + (1 - o) * 10;
              const nx = mapNode.x;
              const half = MAP_SZ / 2;
              return (
                <g opacity={o}>
                  {/* Square card */}
                  <rect x={nx - half} y={ny - half} width={MAP_SZ} height={MAP_SZ}
                    rx={MAP_R} fill={mapBg} stroke={mapStroke} strokeWidth={0.8}
                    filter="url(#hp-shadow-map)" />
                  {/* Swap icon */}
                  <g transform={`translate(${nx - 9},${ny - 9}) scale(0.75)`}>
                    <path d={ICO.swap} fill={mapIcon} />
                  </g>
                  {/* Side connector dots */}
                  <circle cx={nx - half} cy={ny} r={3.5} fill={portColor} />
                  <circle cx={nx + half} cy={ny} r={3.5} fill={portColor} />
                  {/* Label */}
                  <text x={nx} y={ny + half + 14} textAnchor="middle"
                    fill={mapIcon} fontSize={8.5} fontWeight={600}
                    fontFamily="Montserrat, 'DM Sans', sans-serif"
                    opacity={0.7}>
                    Logic Builder
                  </text>
                </g>
              );
            })()}

            {/* ── Database Node (Target) — matches WorkflowAnimation Node db ── */}
            {(() => {
              const ep = phase === 0 ? Math.min(1, Math.max(0, (progress - 0.24) * 5)) : 1;
              const o = ease(ep);
              const ny = dbNode.y + (1 - o) * 10;
              const nx = dbNode.x;
              const hw = NODE_W / 2, hh = NODE_H / 2;
              return (
                <g opacity={o}>
                  {/* Breathing glow ring */}
                  <rect x={nx - hw - 2} y={ny - hh - 2} width={NODE_W + 4} height={NODE_H + 4}
                    rx={NODE_R + 2} fill="none" stroke={dbAccent} strokeWidth={0.8}
                    opacity={phase === 0 && progress > 0.6 ? Math.abs(Math.sin(progress * 8 + 1)) * 0.15 : 0}
                  />
                  {/* Card */}
                  <rect x={nx - hw} y={ny - hh} width={NODE_W} height={NODE_H}
                    rx={NODE_R} fill={nodeBg} stroke="url(#hp-gDb)" strokeWidth={1.2}
                    filter="url(#hp-shadow-db)" />
                  {/* Icon halo */}
                  <circle cx={nx} cy={ny + 1} r={20} fill={dbGlow} />
                  <circle cx={nx} cy={ny + 1} r={15} fill="none" stroke={dbAccent} strokeWidth={0.6} opacity={0.15} />
                  {/* Database icon */}
                  <g transform={`translate(${nx - 12},${ny - 11})`}>
                    <path d={ICO.database} fill={dbIcon} />
                  </g>
                  {/* Connector dot */}
                  <circle cx={nx - hw} cy={ny} r={4} fill={portColor} />
                  {/* Label */}
                  <g>
                    <rect x={nx - 36} y={ny + hh + 10} width={72} height={22}
                      rx={11} fill={labelBg} stroke={labelStroke} strokeWidth={0.8}
                      filter="url(#hp-label-shadow)" />
                    <text x={nx} y={ny + hh + 25} textAnchor="middle"
                      fill={labelText} fontSize={10} fontWeight={600}
                      fontFamily="Montserrat, 'DM Sans', sans-serif">
                      Database
                    </text>
                  </g>
                </g>
              );
            })()}

            {/* Status — top of frame, visible immediately */}
            <text x={CX} y={28} textAnchor="middle"
              fontSize="9" fill={accent} fontWeight="700"
              fontFamily="'DM Sans', sans-serif" letterSpacing="2.5"
              opacity={0.55}>
              DESIGNING WORKFLOW
            </text>
          </g>

          {/* ═══ PHASE 1 — GENERATE: Code editor ═══ */}
          <g opacity={
            phase === 1 ? Math.min(1, progress * 4)
            : phase === 2 ? Math.max(0, 1 - progress * 4)
            : 0
          }>
            {/* Editor chrome */}
            <rect x="24" y="14" width={W - 48} height={H - 40} rx="14" ry="14"
              fill={codeBg} stroke={accentLine} strokeWidth="1" />
            {/* Title bar */}
            <rect x="24" y="14" width={W - 48} height="28" rx="14" ry="14"
              fill={isLight ? 'rgba(99, 102, 241,0.03)' : 'rgba(16,185,129,0.04)'} />
            <rect x="24" y="28" width={W - 48} height="14"
              fill={isLight ? 'rgba(99, 102, 241,0.03)' : 'rgba(16,185,129,0.04)'} />
            {/* Window dots */}
            <circle cx="42" cy="28" r="3" fill={isLight ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.4)'} />
            <circle cx="53" cy="28" r="3" fill={isLight ? 'rgba(234,179,8,0.35)' : 'rgba(234,179,8,0.4)'} />
            <circle cx="64" cy="28" r="3" fill={isLight ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.4)'} />
            {/* File tab */}
            <text x={W - 42} y="31" textAnchor="end" fontSize="8" fill={labelColor}
              fontFamily="'DM Sans', monospace" fontWeight="500" opacity="0.4">
              Workflow1.java
            </text>
            {/* Separator */}
            <line x1="24" y1="42" x2={W - 24} y2="42" stroke={accentLine} strokeWidth="0.5" />

            {/* Code lines */}
            {CODE_TOKENS.map((line, i) => {
              const lp = phase === 1
                ? Math.max(0, Math.min(1, (progress * (CODE_TOKENS.length + 1) - i) * 1.8))
                : 1;
              if (lp <= 0 || !line.text) return null;

              const maxChars = Math.floor(lp * line.text.length);

              // Build visible tspans respecting the typing cursor position
              const spans = [];
              let charsSoFar = 0;
              for (const tok of line.tokens) {
                if (charsSoFar >= maxChars) break;
                const remaining = maxChars - charsSoFar;
                const vis = tok.text.length <= remaining ? tok.text : tok.text.substring(0, remaining);
                charsSoFar += vis.length;
                spans.push({ text: vis, type: tok.type });
              }

              const showCursor = phase === 1 && lp < 1 && lp > 0 && cursorRef.current;

              return (
                <g key={i}>
                  <text x="44" y={60 + i * 24} fontSize="9" fontFamily="monospace"
                    fill={textDim} fontWeight="500" textAnchor="end">
                    {i + 1}
                  </text>
                  <text x={56 + line.indent * 16} y={60 + i * 24}
                    fontSize="12" fontFamily="'JetBrains Mono','Fira Code',monospace"
                    opacity={Math.min(1, lp * 2.5)}>
                    {spans.map((s, j) => (
                      <tspan key={j} fill={syn[s.type]}
                        fontWeight={s.type === 'keyword' || s.type === 'className' ? 700 : 400}
                        fontStyle={s.type === 'annotation' ? 'italic' : 'normal'}>
                        {s.text}
                      </tspan>
                    ))}
                  </text>
                  {showCursor && (
                    <rect
                      x={56 + line.indent * 16 + maxChars * 7.2}
                      y={60 + i * 24 - 11}
                      width="1.5" height="14"
                      fill={accent} opacity="0.7"
                    />
                  )}
                </g>
              );
            })}

            {/* Status — top */}
            <text x={CX} y={28} textAnchor="middle" fontSize="9"
              fill={accent} fontWeight="700" fontFamily="'DM Sans', sans-serif"
              letterSpacing="2.5" opacity="0.55">
              GENERATING NATIVE CODE
            </text>
            {/* Auto badge */}
            <g opacity="0.5">
              <rect x={W - 78} y={16} width={42} height={16} rx={8}
                fill={accent} opacity="0.10" />
              <text x={W - 57} y={27.5} textAnchor="middle" fontSize="7"
                fill={accent} fontWeight="800" fontFamily="'DM Sans', sans-serif"
                letterSpacing="1">AUTO</text>
            </g>
          </g>

          {/* ═══ PHASE 2 — COMPILE: Bytecode ═══ */}
          <g opacity={
            phase === 2 ? Math.min(1, progress * 5)
            : phase === 3 ? Math.max(0, 1 - progress * 4)
            : 0
          }>
            <rect x="24" y="14" width={W - 48} height={H - 40} rx="14" ry="14"
              fill={codeBg} stroke={accentLine} strokeWidth="1" />
            <text x={CX} y={28} textAnchor="middle" fontSize="9" fontFamily="'DM Sans', sans-serif"
              fill={accent} fontWeight="700" letterSpacing="2.5" opacity="0.55">
              COMPILING BYTECODE
            </text>
            {/* Auto badge */}
            <g opacity="0.5">
              <rect x={W - 78} y={16} width={42} height={16} rx={8}
                fill={accent} opacity="0.10" />
              <text x={W - 57} y={27.5} textAnchor="middle" fontSize="7"
                fill={accent} fontWeight="800" fontFamily="'DM Sans', sans-serif"
                letterSpacing="1">AUTO</text>
            </g>

            {BYTE_ROWS.map((row, i) => {
              const rp = phase === 2
                ? Math.max(0, Math.min(1, (progress * (BYTE_ROWS.length + 2) - i) * 1.2))
                : 1;
              const chars = Math.floor(rp * row.length);
              if (chars <= 0) return null;

              return (
                <g key={i}>
                  <text x="44" y={60 + i * 26} fontSize="9.5" fontFamily="monospace"
                    fill={textDim} fontWeight="600">
                    {String(i * 8).padStart(4, '0')}
                  </text>
                  <text x="82" y={60 + i * 26} fontSize="13"
                    fontFamily="'JetBrains Mono','Fira Code',monospace"
                    fontWeight="500" fill={accent}
                    opacity={Math.min(0.65, rp * 1.2)}
                    letterSpacing="1.5">
                    {row.substring(0, chars)}
                  </text>
                </g>
              );
            })}

            {phase === 2 && (
              <>
                <rect x="60" y={H - 46} width={W - 120} height="4" rx="2" fill={accentSoft} />
                <rect x="60" y={H - 46} width={(W - 120) * progress} height="4" rx="2"
                  fill={accent} opacity="0.6" />
                <text x={CX} y={H - 16} textAnchor="middle" fontSize="9.5"
                  fill={accent} fontWeight="600" fontFamily="'DM Sans', sans-serif"
                  letterSpacing="2" opacity="0.7">
                  COMPILING — {Math.floor(progress * 100)}%
                </text>
              </>
            )}
          </g>

          {/* ═══ PHASE 3 — DEPLOY: Server Infrastructure ═══ */}
          <g opacity={phase === 3 ? Math.min(1, progress * 4) : 0}>

            {/* Mesh lines between servers (draw first so behind nodes) */}
            {(() => {
              const meshP = phase === 3 ? Math.max(0, Math.min(1, (progress - 0.45) / 0.25)) : 0;
              const ep = ease(meshP);
              if (ep <= 0) return null;

              const pairs = [
                [0, 1], [1, 2], [0, 2],
              ];
              return pairs.map(([a, b], i) => {
                const sa = servers[a], sb = servers[b];
                const cx1 = sa.x + SRV_W / 2, cy1 = sa.y + SRV_H / 2;
                const cx2 = sb.x + SRV_W / 2, cy2 = sb.y + SRV_H / 2;
                const lineP = Math.max(0, Math.min(1, (ep - i * 0.2) * 3));
                return (
                  <g key={`mesh-${i}`}>
                    <line
                      x1={cx1} y1={cy1}
                      x2={cx1 + (cx2 - cx1) * lineP} y2={cy1 + (cy2 - cy1) * lineP}
                      stroke={meshLine} strokeWidth="1.2" strokeDasharray="4,4"
                    />
                    {/* Data pulse traveling along mesh */}
                    {lineP >= 1 && progress > 0.72 && (
                      <circle
                        cx={cx1 + (cx2 - cx1) * ((progress * (3 + i * 0.4)) % 1)}
                        cy={cy1 + (cy2 - cy1) * ((progress * (3 + i * 0.4)) % 1)}
                        r="2.5" fill={accent} filter="url(#hp-glow)" opacity="0.5"
                      />
                    )}
                  </g>
                );
              });
            })()}

            {/* Server nodes */}
            {servers.map((srv, i) => {
              const enterP = phase === 3
                ? Math.max(0, Math.min(1, (progress - i * 0.12) / 0.2))
                : 0;
              const ep = ease(enterP);
              const sy = srv.y + (1 - ep) * 15; // slide up
              const isOnline = progress > 0.25 + i * 0.12;
              const statusP = Math.max(0, Math.min(1, (progress - 0.25 - i * 0.12) / 0.15));

              return (
                <g key={`srv-${i}`} opacity={ep}>
                  {/* Online glow */}
                  {isOnline && (
                    <rect x={srv.x - 3} y={sy - 3} width={SRV_W + 6} height={SRV_H + 6}
                      rx={SRV_R + 3} fill="none" stroke={dotGreen} strokeWidth={0.6}
                      opacity={statusP * 0.2}
                    />
                  )}
                  {/* Server card */}
                  <rect x={srv.x} y={sy} width={SRV_W} height={SRV_H}
                    rx={SRV_R} fill={isOnline ? serverOn : serverOff}
                    stroke={isOnline ? accent : serverStroke} strokeWidth={isOnline ? 1.2 : 0.8}
                    filter="url(#hp-srv-shadow)"
                  />

                  {/* Server icon (simplified rack) */}
                  <g transform={`translate(${srv.x + 12},${sy + 12})`}>
                    {/* Top unit */}
                    <rect x="0" y="0" width="28" height="8" rx="2"
                      fill="none" stroke={isOnline ? accent : labelColor}
                      strokeWidth="1" opacity={isOnline ? 0.5 : 0.25} />
                    <circle cx="24" cy="4" r="2"
                      fill={isOnline ? dotGreen : dotOff}
                      opacity={isOnline ? ease(statusP) : 0.4} />
                    {/* Middle unit */}
                    <rect x="0" y="12" width="28" height="8" rx="2"
                      fill="none" stroke={isOnline ? accent : labelColor}
                      strokeWidth="1" opacity={isOnline ? 0.5 : 0.25} />
                    <circle cx="24" cy="16" r="2"
                      fill={isOnline ? dotGreen : dotOff}
                      opacity={isOnline ? ease(statusP) * 0.9 : 0.4} />
                    {/* Bottom unit */}
                    <rect x="0" y="24" width="28" height="8" rx="2"
                      fill="none" stroke={isOnline ? accent : labelColor}
                      strokeWidth="1" opacity={isOnline ? 0.5 : 0.25} />
                    <circle cx="24" cy="28" r="2"
                      fill={isOnline ? dotGreen : dotOff}
                      opacity={isOnline ? ease(statusP) * 0.8 : 0.4} />
                  </g>

                  {/* Utilization bars */}
                  {isOnline && (
                    <g opacity={ease(statusP) * 0.45}>
                      <rect x={srv.x + 50} y={sy + 14} width="28" height="3" rx="1.5"
                        fill={accentSoft} />
                      <rect x={srv.x + 50} y={sy + 14}
                        width={28 * Math.min(1, statusP * (0.6 + i * 0.15))} height="3" rx="1.5"
                        fill={accent} opacity="0.6" />
                      <rect x={srv.x + 50} y={sy + 22} width="28" height="3" rx="1.5"
                        fill={accentSoft} />
                      <rect x={srv.x + 50} y={sy + 22}
                        width={28 * Math.min(1, statusP * (0.4 + i * 0.2))} height="3" rx="1.5"
                        fill={accent} opacity="0.5" />
                      <rect x={srv.x + 50} y={sy + 30} width="28" height="3" rx="1.5"
                        fill={accentSoft} />
                      <rect x={srv.x + 50} y={sy + 30}
                        width={28 * Math.min(1, statusP * (0.3 + i * 0.1))} height="3" rx="1.5"
                        fill={accent} opacity="0.4" />
                    </g>
                  )}

                  {/* Label */}
                  <text x={srv.x + SRV_W / 2} y={sy + SRV_H + 15}
                    textAnchor="middle" fontSize="9" fill={labelColor}
                    fontWeight="600" fontFamily="'DM Sans', sans-serif"
                    letterSpacing="0.3" opacity={ep * 0.7}>
                    {srv.label}
                  </text>
                </g>
              );
            })}

            {/* Central status indicator */}
            {progress > 0.7 && (() => {
              const sp = Math.min(1, (progress - 0.7) / 0.15);
              const ep = ease(sp);
              return (
                <g opacity={ep}>
                  {/* Success ring */}
                  <circle cx={CX} cy={CY - 3} r={14}
                    fill={isLight ? 'rgba(34,197,94,0.06)' : 'rgba(74,222,128,0.06)'}
                    stroke={dotGreen} strokeWidth="1.2" opacity={0.6}
                  />
                  {/* Checkmark */}
                  <path
                    d={`M${CX - 5},${CY - 3} L${CX - 1},${CY + 1} L${CX + 6},${CY - 7}`}
                    fill="none" stroke={dotGreen} strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                    opacity={ep * 0.75}
                  />
                </g>
              );
            })()}

            {/* Status — top, visible immediately */}
            <text x={CX} y={28} textAnchor="middle" fontSize="9"
              fill={accent} fontWeight="700" fontFamily="'DM Sans', sans-serif"
              letterSpacing="2.5" opacity="0.55">
              {progress < 0.25 ? 'DEPLOYING TO SERVERS'
               : progress < 0.45 ? 'PROVISIONING CLUSTER'
               : progress < 0.7 ? 'ESTABLISHING MESH'
               : 'ALL INSTANCES LIVE'}
            </text>
            {/* Auto badge */}
            <g opacity="0.5">
              <rect x={W - 78} y={16} width={42} height={16} rx={8}
                fill={accent} opacity="0.10" />
              <text x={W - 57} y={27.5} textAnchor="middle" fontSize="7"
                fill={accent} fontWeight="800" fontFamily="'DM Sans', sans-serif"
                letterSpacing="1">AUTO</text>
            </g>
          </g>
        </svg>
      </Box>
    </Box>
  );
}
